// src/mixins/sectionManagerMixin.js
import { hasSection, hasSubSection } from '@/assets/settings/SectionByDossierType'

export default {
  provide() {
    return {
      toggleSectionVisibility: this.toggleSectionVisibility,
    }
  },

  computed: {
    isFormEditorMode() {
      return this.$store.getters['metadata/getGlobalFormEditionMode']
    },

    // Can be overridden in components that determine their page key dynamically
    resolvedPageKey() {
      return this.$options.sectionPageKey
    },

    // All sections eligible for the current dossier type (respects visible predicates)
    allPageSections() {
      const defs = this.$options.sectionDefs ?? []
      const pageKey = this.resolvedPageKey
      if (defs.length > 0 && this.dossier?.type && !hasSection(this.dossier.type, pageKey)) {
        console.error(
          `[SectionManagerMixin] sectionPageKey "${pageKey}" not found in SectionByDossierType.js for dossier type "${this.dossier.type}". All sections will be hidden.`
        )
      }
      return defs.filter((section) => {
        if (!hasSubSection(this.dossier?.type, pageKey, section.key)) return false
        if (typeof section.visible === 'function' && !section.visible(this)) return false
        return true
      })
    },

    visibleSections() {
      const pageKey = this.resolvedPageKey
      const order = this.$store.getters['metadata/getSectionsOrder'](pageKey)

      /**
       * Resolves the display order for a section.
       * Uses `section.orderKey` (e.g. an EventType code) to look up position in the
       * stored order array, falling back to `section.key`. If not found, uses
       * `section.defaultOrder` to pin sections that are outside the ordering system
       * (e.g. `funeralInfos` at -1 to stay first, `pacemaker` at 1 to follow thanato).
       *
       * @param {{ key: string, orderKey?: string, defaultOrder?: number, props?: Function|object }} section
       * @param {boolean} isHidden
       */
      const withMeta = (section, isHidden) => {
        const orderKey = section.orderKey ?? section.key
        const indexInOrder = order.indexOf(orderKey)
        return {
          ...section,
          isHidden,
          resolvedProps: typeof section.props === 'function' ? section.props(this) : (section.props ?? {}),
          order: order.length > 0
            ? (indexInOrder === -1 ? (section.defaultOrder ?? order.length) : indexInOrder)
            : (section.defaultOrder ?? 0),
        }
      }

      if (this.isFormEditorMode) {
        // Show ALL sections; hidden ones keep isHidden: true for the toggle bar UI
        const hiddenKeys =
          this.$store.getters['metadata/getGlobalFormCurrentFormSettings']?.sections?.[pageKey] ?? []
        return this.allPageSections
          .map((section) => withMeta(section, hiddenKeys.includes(section.key)))
          .sort((sectionA, sectionB) => sectionA.order - sectionB.order)
      }

      // Normal mode: only non-hidden sections
      const funeralHomeId = this.dossier?.funeral_home_id
      const hiddenKeys = this.$store.getters['metadata/getHiddenSections'](pageKey, funeralHomeId)
      return this.allPageSections
        .filter((section) => !hiddenKeys.includes(section.key))
        .map((section) => withMeta(section, false))
        .sort((sectionA, sectionB) => sectionA.order - sectionB.order)
    },

    // Used by HiddenSectionChips (only in normal mode)
    hiddenPageSections() {
      if (this.isFormEditorMode) return []
      const pageKey = this.resolvedPageKey
      const funeralHomeId = this.dossier?.funeral_home_id
      const hiddenKeys = this.$store.getters['metadata/getHiddenSections'](pageKey, funeralHomeId)
      return this.allPageSections.filter((section) => hiddenKeys.includes(section.key))
    },
  },

  methods: {
    toggleSectionVisibility(sectionKey) {
      if (!this.isFormEditorMode) {
        this.activateFormEditorMode()
        this.$nextTick(() => {
          const el = document.querySelector(`[data-section-key="${sectionKey}"]`)
          if (!el) return
          const container = document.getElementById('dossier_layout_container')
          if (container) {
            container.scrollTo({ top: el.offsetTop - 16, behavior: 'smooth' })
          } else {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
        return
      }
      const pageKey = this.resolvedPageKey
      this.$store.commit('metadata/toggleFormConfigSection', { pageKey, sectionKey })
    },

    activateFormEditorMode() {
      if (!this.$can('manage_settings')) return
      if (this.isFormEditorMode) {
        this.$store.commit('metadata/toggleGlobalFormEditionMode', false)
        return
      }
      const funeralHomeId = this.dossier?.funeral_home_id
      if (!funeralHomeId) return
      this.$store.dispatch('metadata/toggleGlobalFormEditionModeForFuneralHome', {
        funeral_home_id: funeralHomeId,
      })
    },

    // Helper for pages with inline sections (not using v-for pattern)
    isSectionHidden(sectionKey) {
      const pageKey = this.resolvedPageKey
      if (this.isFormEditorMode) {
        return (
          this.$store.getters['metadata/getGlobalFormCurrentFormSettings']?.sections?.[pageKey] ?? []
        ).includes(sectionKey)
      }
      return this.$store.getters['metadata/getHiddenSections'](
        pageKey,
        this.dossier?.funeral_home_id
      ).includes(sectionKey)
    },
  },
}
